// ── universes.js ─────────────────────────────────────────────────────────────
// Data + progress-math for the Universes feature (TV-native franchise trackers:
// One Chicago, NCIS, Law & Order, FBI, Star Trek, Star Wars). Backed by the
// existing curator_lists/curator_list_shows tables (kind='universe'), owned by
// the NextEp system account — NOT a new schema. Progress math is intentionally
// a thin wrapper around lib/episode.js's canonical watchedEpisodes/totalEpisodes
// so a Universe's numbers can never disagree with ShowDetail/MyList's own bars.
import { supabase } from './supabase'
import { getValidSeasons, totalEpisodes, watchedEpisodes } from './episode'

// Fetch every published Universe (title/emoji/description) with its ordered
// shows. Public + system-owned, so no auth is required to read them — the hub
// itself gates on `user` only to decide whether to show live progress vs. a
// "sign in to track your progress" nudge.
// Returns the array of universes on success, or { error: true } if either
// query fails — callers must check for that shape before treating the result
// as "no universes" (an empty array is a legitimate, different state).
export async function fetchUniverses() {
  const { data: lists, error: listErr } = await supabase
    .from('curator_lists')
    .select('id, title, description, emoji, show_count, created_at')
    .eq('kind', 'universe')
    .eq('status', 'approved')
    .order('created_at', { ascending: true })
  if (listErr) { console.error('[universes] load lists failed', listErr); return { error: true } }
  if (!lists?.length) return []

  const { data: shows, error: showErr } = await supabase
    .from('curator_list_shows')
    .select('list_id, show_id, show_data, sort_order, added_at')
    .in('list_id', lists.map(l => l.id))
    .order('sort_order', { ascending: true })
    .order('added_at', { ascending: true })
  // A failure here can't be swallowed: continuing would render every universe
  // with shows:[] (0 shows / 0%), indistinguishable from genuinely-empty data.
  if (showErr) { console.error('[universes] load shows failed', showErr); return { error: true } }

  const byList = new Map()
  for (const row of shows || []) {
    if (!byList.has(row.list_id)) byList.set(row.list_id, [])
    byList.get(row.list_id).push(row)
  }
  return lists.map(l => ({
    ...l,
    shows: (byList.get(l.id) || []).map(r => r.show_data),
  }))
}

// Percent-watched, rounded for display but never claiming "100%" unless it truly
// is: plain Math.round((711/714)*100) = 100 even though 3 episodes remain, which
// read as a broken progress bar on the Home Canon card (Jason 2026-08-15, "711 of
// 714 · 100%"). Round normally, but clamp a not-actually-done total to 99 — and
// symmetrically never show 0% for a show that HAS a watched episode (the same
// rounding can drop a tiny nonzero fraction to 0, which reads as "no progress").
function capPct(watched, total) {
  if (!total) return 0
  const pct = Math.round((watched / total) * 100)
  if (watched >= total) return 100
  if (pct >= 100) return 99
  if (pct <= 0 && watched > 0) return 1
  return pct
}

// Per-show progress, reusing the app's one canonical episode-count math. A show
// the user hasn't tracked at all still counts (0 watched / its total) so the
// hub-level aggregate is honest about the whole franchise, not just started shows.
export function showProgress(show, wl) {
  const status = wl?.statuses?.[show.id]
  const progress = wl?.progress?.[show.id]
  const seasons = getValidSeasons(show?.seasons)
  const total = totalEpisodes(seasons)
  const watched = status ? watchedEpisodes(progress, seasons, status) : 0
  const pct = capPct(watched, total)
  return { watched, total, pct, status: status || null, tracked: !!status }
}

// Aggregate a universe's shows into one "X of Y watched" figure + overall pct.
export function universeProgress(shows, wl) {
  let watched = 0, total = 0, tracked = 0
  const perShow = (shows || []).map(s => {
    const p = showProgress(s, wl)
    watched += p.watched
    total += p.total
    if (p.tracked) tracked++
    return { show: s, ...p }
  })
  const pct = capPct(watched, total)
  return { watched, total, pct, tracked, perShow }
}

export function posterUrl(path, size = 'w342') {
  return path ? `https://image.tmdb.org/t/p/${size}${path}` : null
}
